<template>
  <Modal
    :show="show"
    title="Add a note to your invitation"
    size="sm"
    :dialog-max-width="'500px'"
    :backdrop-opacity="0.5"
    :closeOnBackdrop="false"
    :zIndex="10060"
    @close="emit('close')"
  >
    <template #header>
      <div class="connection-modal__header-inner">
        <h2 id="modal-title" class="connection-modal__title">Add a note to your invitation</h2>
        <p v-if="contactName" class="connection-modal__subtitle">
          Sending invitation to {{ contactName }}
        </p>
      </div>
    </template>

    <div class="connection-modal__body">
      <p class="connection-modal__guidance">
        LinkedIn members are more likely to accept invitations that include a personal note.
      </p>

      <div class="connection-modal__form-group">
        <Textarea
          id="connection-message"
          v-model="noteText"
          label="Personalized Message (Optional)"
          placeholder="Ex: We know each other from..."
          :max-length="200"
          :show-char-count="false"
          :disabled="isLoading"
          :rows="5"
          :resize="'vertical'"
          class="connection-modal__textarea"
        />

        <div class="connection-modal__ai-row">
          <div class="connection-modal__ai-buttons">
            <button
              ref="aiRefineButtonRef"
              type="button"
              class="connection-modal__ai-btn connection-modal__ai-btn--refine"
              :disabled="isGeneratingAiCustom"
              :title="isGeneratingAiCustom ? 'Generating...' : 'AI Refine'"
              @click="showAiPanel = !showAiPanel"
            >
              <Icon icon="mdi:sparkles" class="connection-modal__ai-btn-icon" />
              <span class="connection-modal__ai-btn-text">AI Refine</span>
            </button>
          </div>
          <span
            class="connection-modal__char-count"
            :class="{ 'connection-modal__char-count--error': noteText.length > 200 }"
          >
            {{ noteText.length }}/200
          </span>
        </div>

        <AiRefinePopover
          :show="showAiPanel"
          :trigger-el="aiRefineButtonRef"
          :disabled="isGeneratingAiCustom"
          @generate="handleAiCustomizationGenerate"
          @close="showAiPanel = false"
        />
      </div>

      <div v-if="error" class="connection-modal__error" role="alert">
        <Icon icon="mdi:alert-circle" class="connection-modal__error-icon" aria-hidden="true" />
        <span>{{ error }}</span>
      </div>
    </div>

    <template #footer>
      <div class="connection-modal__actions">
        <Button variant="secondary" @click="emit('close')" :disabled="isLoading"> Cancel </Button>
        <Button variant="primary" :loading="isLoading" :disabled="isLoading" @click="handleSend">
          Send Invitation
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { ref, watch } from "vue";
import { Button, Modal, Textarea } from "@/components/ui";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useToast } from "@/composables/ui/useToast";

interface Props {
  show: boolean;
  isLoading?: boolean;
  error?: string | null;
  contactName?: string | null;
  initialMessage?: string;
  contactId?: string;
  icpId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  error: null,
  contactName: null,
  initialMessage: "",
});

const emit = defineEmits<{
  close: [];
  send: [data: { message?: string }];
}>();

const noteText = ref("");
const showAiPanel = ref(false);
const isGeneratingAiCustom = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const { success, error: toastError } = useToast();

const handleAiCustomizationGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contactId || "",
      icpId: props.icpId,
      channel: "linkedin",
      currentDraft: noteText.value,
      customInstructions:
        `${payload.customInstructions || ""} (Keep the message under 200 characters for a LinkedIn connection note)`.trim(),
    });

    const refinedText = response.body || "";
    // Still slice just in case the AI ignores the instruction
    noteText.value = refinedText.length > 200 ? refinedText.slice(0, 200) : refinedText;
    showAiPanel.value = false;
    success("Message refined");
  } catch (err) {
    console.error("Failed to refine LinkedIn message:", err);
    toastError("Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

watch(
  () => props.show,
  (isShowing) => {
    if (isShowing) {
      noteText.value = (props.initialMessage || "").slice(0, 200);
      showAiPanel.value = false;
    }
  }
);

const handleSend = () => {
  if (noteText.value.length > 200) {
    toastError("Message exceeds 200 character limit");
    return;
  }
  emit("send", {
    message: noteText.value.trim() || undefined,
  });
};
</script>

<style scoped>
.connection-modal__header-inner {
  flex: 1;
  min-width: 0;
}

.connection-modal__title {
  margin: 0 0 4px 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1a1a2e;
}

.connection-modal__subtitle {
  margin: 0;
  font-size: 0.8125rem;
  color: #646464;
  line-height: 1.4;
}

.connection-modal__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
}

.connection-modal__guidance {
  margin: 0;
  font-size: 0.875rem;
  color: #646464;
  line-height: 1.5;
}

.connection-modal__form-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.connection-modal__textarea {
  width: 100%;
}

:deep(.connection-modal__textarea .ui-textarea__field) {
  font-size: 0.8125rem;
}

.connection-modal__ai-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}

.connection-modal__ai-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.connection-modal__char-count {
  font-size: 0.75rem;
  color: #9ca3af;
  transition: color 0.2s;
}

.connection-modal__char-count--error {
  color: #dc2626;
  font-weight: 600;
}

.connection-modal__ai-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.7rem;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
}

.connection-modal__ai-btn--refine {
  background: rgba(107, 114, 128, 0.12);
  color: #4b5563;
}

.connection-modal__ai-btn--refine:hover:not(:disabled) {
  background: rgba(107, 114, 128, 0.2);
  color: #374151;
}

.connection-modal__ai-btn--refine .connection-modal__ai-btn-icon {
  color: #4b5563;
}

.connection-modal__ai-btn--refine:hover:not(:disabled) .connection-modal__ai-btn-icon {
  color: #374151;
}

.connection-modal__ai-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.connection-modal__ai-btn-icon {
  font-size: 0.75rem;
}

.connection-modal__ai-btn-text {
  font-size: 0.7rem;
  font-weight: 500;
  line-height: 1;
}

.connection-modal__error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 0.875rem;
}

.connection-modal__error-icon {
  font-size: 1.125rem;
  flex-shrink: 0;
}

.connection-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}
</style>
